import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import {
    mdiCheckCircle,
    mdiCheckCircleOutline,
    mdiArrowULeftTop,
    mdiAccountPlus,
    mdiAccountMultiplePlusOutline,
    mdiArrowLeft,
    mdiSchool,
    mdiCreditCardCheck,
    mdiCreditCardClockOutline,
    mdiCreditCardSyncOutline,
    mdiCreditCardPlusOutline,
    mdiDanceBallroom,
    mdiCalendarRemove,
    mdiHelpCircle,
    mdiHelpCircleOutline,
    mdiLockOutline,
    mdiLockOpenOutline,
    mdiChevronRight,
} from "@mdi/js";
import { useAuth } from "../../contexts/AuthContext";
import { useMobileMenu } from "../../hooks/useMobileMenu";
import { toast } from "react-toastify";
import AddStudentModal from "../../components/classes/AddStudentModal";
import AdminPaymentModal from "../../components/classes/AdminPaymentModal";
import ManualChargeModal from "../../components/classes/ManualChargeModal";
import CollectAdjustmentModal from "../../components/classes/CollectAdjustmentModal";
import ChangeRoleModal from "../../components/classes/ChangeRoleModal";
import SessionOverrideModal from "../../components/classes/SessionOverrideModal";
import CancelClassesModal from "../../components/classes/CancelClassesModal";
import CheckInHelpModal from "../../components/CheckInHelpModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import { classApi } from "../../api/classes";
import { organizationApi } from "../../api/organization";
import StudentAvatarUpload from "../../components/shared/StudentAvatarUpload";
import StudentPhotoPreviewModal from "../../components/shared/StudentPhotoPreviewModal";
import { cacheImage, getCachedImage, getOrFetchImage, base64ToBlobUrl } from "../../utils/imageCache";
import "../Common.css";
import "../Dashboard.css";
import "../header.css";
import "./ClassCheckIn.css";
/**
 * ClassCheckIn - iPad-optimised two-panel check-in screen
 *
 * Architecture:
 *  - Operates on the CURRENT WEEK only (Mon–Sun). No cross-week navigation.
 *  - Session gate: admin presses "Start Session" → session created, instructor
 *    confirmed. Only then is the student list revealed and check-in enabled.
 *    This means checkIn is never called without an existing session record.
 *  - Past days (selectedDate < today): locked by default. Admin must unlock
 *    with confirmation. Lock state persists via class-sessions.isLocked.
 *  - Short courses: no locking — short courses span multiple days.
 */
const ClassCheckIn = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    useMobileMenu();

    const [selectedDate, setSelectedDate] = useState(() => {
        const d = new Date();
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
    });
    const [tonightClasses, setTonightClasses] = useState([]);
    const [selectedClassId, setSelectedClassId] = useState(null);
    const [enrolments, setEnrolments] = useState([]);
    const [checkedInIds, setCheckedInIds] = useState(new Set());
    const [isLoadingClasses, setIsLoadingClasses] = useState(true);
    const [isLoadingEnrolments, setIsLoadingEnrolments] = useState(false);
    const [flashingId, setFlashingId] = useState(null);
    const cardRefs = useRef({}); // id → DOM node
    const flipPending = useRef(null); // { id, firstRect } — set before state update, consumed in useLayoutEffect
    const checkedInOrder = useRef([]); // tracks insertion order for newest-first sort
    const [showAddModal, setShowAddModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [paymentModalStudent, setPaymentModalStudent] = useState(null);
    // Walk-in drop-in fee = a manual adjustment: create it (blue), then collect it (orange)
    // via mark-paid / QR / email. Not a term package.
    const [createAdjustmentFor, setCreateAdjustmentFor] = useState(null);
    const [collectFor, setCollectFor] = useState(null);
    const [changeRoleEnrolment, setChangeRoleEnrolment] = useState(null);
    const [showOverrideModal, setShowOverrideModal] = useState(false);
    const [cancelClassesOpen, setCancelClassesOpen] = useState(false);
    const [helpOpen, setHelpOpen] = useState(false);
    const [helpIconHovered, setHelpIconHovered] = useState(false);
    const [orgAdmins, setOrgAdmins] = useState([]);
    const [sessionExists, setSessionExists] = useState(false);
    const [isStartingSession, setIsStartingSession] = useState(false);
    const [currentSession, setCurrentSession] = useState(null);
    const [isLockingSession, setIsLockingSession] = useState(false);
    const [confirmModal, setConfirmModal] = useState(null); // { title, message, variant, onConfirm }

    const loadTonightClasses = useCallback(async (date = null) => {
        setIsLoadingClasses(true);
        try {
            const response = await classApi.getTonightClasses(date);
            if (response.ok) {
                const classes = (response.data.classes || []).sort((a, b) => a.name.localeCompare(b.name));
                setTonightClasses(classes);
                if (classes.length > 0) {
                    setSelectedClassId(classes[0].id);
                } else {
                    setSelectedClassId(null);
                }
            } else {
                toast.error("Failed to load tonight's classes");
            }
        } catch {
            toast.error("Failed to load tonight's classes");
        } finally {
            setIsLoadingClasses(false);
        }
    }, []);

    const loadEnrolments = useCallback(async (classId, dateStr) => {
        if (!classId) return;
        setIsLoadingEnrolments(true);
        try {
            const [enrolRes, sessionRes] = await Promise.all([
                classApi.getEnrolments(classId, dateStr),
                dateStr ? classApi.getSessions(classId, dateStr, dateStr) : Promise.resolve({ ok: false }),
            ]);
            if (enrolRes.ok) {
                const list = enrolRes.data.enrolments || [];
                setEnrolments(list);
                const alreadyChecked = new Set(list.filter(e => e.checkedInToday).map(e => e.id));
                setCheckedInIds(alreadyChecked);
            } else {
            }
            // Session exists if the backend has a record for today — regardless of check-ins
            const session = sessionRes.ok ? (sessionRes.data.sessions || [])[0] || null : null;
            const sessionExistsOnServer = !!session;
            const hasCheckedIn = enrolRes.ok && (enrolRes.data.enrolments || []).some(e => e.checkedInToday);
            setSessionExists(sessionExistsOnServer || hasCheckedIn);
            setCurrentSession(session);
        } catch {
            toast.error("Failed to load students");
        } finally {
            setIsLoadingEnrolments(false);
        }
    }, []);

    const loadShortCourseEnrolments = useCallback(async (shortCourseId, dateStr) => {
        if (!shortCourseId) return;
        setIsLoadingEnrolments(true);
        try {
            const [enrolRes, sessionRes] = await Promise.all([
                classApi.getShortCourseEnrolments(shortCourseId, dateStr),
                dateStr ? classApi.getSessions(shortCourseId, dateStr, dateStr) : Promise.resolve({ ok: false }),
            ]);
            if (enrolRes.ok) {
                const list = enrolRes.data.enrolments || [];
                setEnrolments(list);
                const alreadyChecked = new Set(list.filter(e => e.checkedInToday).map(e => e.id));
                setCheckedInIds(alreadyChecked);
                const hasCheckedIn = list.some(e => e.checkedInToday);
                const sessionExistsOnServer = sessionRes.ok && (sessionRes.data.sessions || []).length > 0;
                setSessionExists(sessionExistsOnServer || hasCheckedIn);
            }
        } catch {
            toast.error("Failed to load students");
        } finally {
            setIsLoadingEnrolments(false);
        }
    }, []);

    const toLocalIso = d => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, "0");
        const day = String(d.getDate()).padStart(2, "0");
        return `${y}-${m}-${day}`;
    };

    const todayIso = toLocalIso(new Date());

    const getWeekDays = () => {
        const today = new Date();
        // Get Monday of current week (ISO week starts Monday)
        const dayOfWeek = today.getDay(); // 0=Sun, 1=Mon, ...
        const diffToMonday = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        const monday = new Date(today);
        monday.setDate(today.getDate() + diffToMonday);
        const labels = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
        return labels.map((label, i) => {
            const d = new Date(monday);
            d.setDate(monday.getDate() + i);
            return { iso: toLocalIso(d), label, dayIndex: i };
        });
    };

    const selectDay = iso => {
        // Clear student state immediately so stale data isn't visible while loading
        setEnrolments([]);
        setCheckedInIds(new Set());
        setSelectedClassId(null);
        checkedInOrder.current = [];
        setSelectedDate(iso);
        loadTonightClasses(iso);
    };

    // Stale-tab guard: when the page regains focus or visibility (e.g. an iPad
    // left open from a previous day is picked up again), snap selectedDate
    // forward to today if it's behind. The week strip already auto-rebuilds
    // from `new Date()` each render — this fixes the orphan case where the
    // initial useState ran on a previous day and selectedDate is stale.
    useEffect(() => {
        const snapToToday = () => {
            const today = toLocalIso(new Date());
            if (selectedDate < today) {
                setSelectedDate(today);
                setSelectedClassId(null);
                setEnrolments([]);
                setCheckedInIds(new Set());
                checkedInOrder.current = [];
                // Reload the class column for today — otherwise the label flips
                // to today's date while the column keeps showing the prior day's
                // classes (no effect watches selectedDate to refetch).
                loadTonightClasses(today);
            }
        };
        window.addEventListener("focus", snapToToday);
        document.addEventListener("visibilitychange", snapToToday);
        return () => {
            window.removeEventListener("focus", snapToToday);
            document.removeEventListener("visibilitychange", snapToToday);
        };
    }, [selectedDate, loadTonightClasses]);

    // Load org admins once for instructor override
    useEffect(() => {
        if (!user?.organisationId) return;
        organizationApi.getOrgAdmins(user.organisationId).then(res => {
            if (res.ok) setOrgAdmins(res.data.admins || []);
        });
    }, [user?.organisationId]);

    useEffect(() => {
        loadTonightClasses(selectedDate);
    }, [loadTonightClasses]);

    useEffect(() => {
        if (selectedClassId) {
            checkedInOrder.current = [];
            setEnrolments([]);
            setCheckedInIds(new Set());
            setSessionExists(false);
            setCurrentSession(null);
            // Only check if a session already exists — do NOT load students yet.
            // Students are loaded when the admin clicks "Start Session" (or if a
            // session is already running, e.g. returning to a class mid-session).
            if (selectedDate) {
                classApi.getSessions(selectedClassId, selectedDate, selectedDate).then(sessionRes => {
                    const session = sessionRes.ok ? (sessionRes.data.sessions || [])[0] || null : null;
                    if (session) {
                        // Session already started (returning admin) — load students now
                        setSessionExists(true);
                        setCurrentSession(session);
                        const cls = tonightClasses.find(c => c.id === selectedClassId);
                        if (cls?.classType === "short-course") {
                            loadShortCourseEnrolments(selectedClassId, selectedDate);
                        } else {
                            loadEnrolments(selectedClassId, selectedDate);
                        }
                    }
                });
            }
        } else {
            // No class selected (e.g. no classes on this day) — clear all student state
            setEnrolments([]);
            setCheckedInIds(new Set());
            setSessionExists(false);
            setCurrentSession(null);
            checkedInOrder.current = [];
        }
    }, [selectedClassId, loadEnrolments, loadShortCourseEnrolments, tonightClasses, selectedDate]);

    const isShortCourse = () => tonightClasses.find(c => c.id === selectedClassId)?.classType === "short-course";
    const hasPrimaryInstructors = () =>
        (tonightClasses.find(c => c.id === selectedClassId)?.primaryInstructors?.length || 0) > 0;

    const handleStartSession = async () => {
        if (!selectedClassId || !selectedDate) return;
        setIsStartingSession(true);
        try {
            const r = isShortCourse()
                ? await classApi.createShortCourseSession(selectedClassId, selectedDate)
                : await classApi.createSession(selectedClassId, selectedDate);
            if (r.ok) {
                setSessionExists(true);
                setCurrentSession(r.data?.session || null);
                // Load students fresh now that session has started
                checkedInOrder.current = [];
                if (isShortCourse()) {
                    loadShortCourseEnrolments(selectedClassId, selectedDate);
                } else {
                    loadEnrolments(selectedClassId, selectedDate);
                }
                toast.success("Session started");
            } else {
                toast.error(r.data?.message || "Failed to start session");
            }
        } catch {
            toast.error("Failed to start session");
        } finally {
            setIsStartingSession(false);
        }
    };

    // FLIP: after checkedInIds changes, find the card that just moved and animate it
    useLayoutEffect(() => {
        if (!flipPending.current) return;
        const { id, firstRect } = flipPending.current;
        flipPending.current = null;
        const node = cardRefs.current[id];
        if (!node) return;
        const lastRect = node.getBoundingClientRect();
        const deltaY = firstRect.top - lastRect.top;
        if (Math.abs(deltaY) < 2) return; // no meaningful movement
        // Scale duration by distance: ~1px per ms, clamped 280–650ms
        const duration = Math.min(650, Math.max(280, Math.abs(deltaY)));
        // Invert: jump card back to where it was (no transition)
        node.style.transition = "none";
        node.style.transform = `translateY(${deltaY}px)`;
        // Play: on next frame, animate to natural position
        requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                node.style.transition = `transform ${duration}ms cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
                node.style.transform = "translateY(0)";
                // Cleanup after transition ends
                const cleanup = () => {
                    node.style.transition = "";
                    node.style.transform = "";
                    node.removeEventListener("transitionend", cleanup);
                };
                node.addEventListener("transitionend", cleanup);
            });
        });
    });

    // DEV: slow-motion scale — set to 1 for production, match --anim-scale in ClassCheckIn.css
    const ANIM_SCALE = 1; /* set to 4 for slow-mo testing */

    const handleCheckIn = async enrolment => {
        if (isLocked) return;
        const id = enrolment.id;
        // Bounce the check button
        setFlashingId(id);
        setTimeout(() => setFlashingId(null), 400 * ANIM_SCALE);
        // Delay card move so the flash and button bounce play while card is stationary
        const node = cardRefs.current[id];
        const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
        const delay = prefersReduced ? 0 : 120 * ANIM_SCALE;
        setTimeout(() => {
            // Capture card's current position BEFORE the DOM move (FLIP "First")
            if (node && !prefersReduced) {
                flipPending.current = { id, firstRect: node.getBoundingClientRect() };
            }
            // Track insertion order for newest-first sort (prepend)
            checkedInOrder.current = [id, ...checkedInOrder.current.filter(x => x !== id)];
            // Move card to checked-in list — triggers re-render + useLayoutEffect
            setCheckedInIds(prev => new Set([...prev, id]));
        }, delay);
        try {
            // Preserve the attendance type when checking in an expected walk-in / make-up
            // so it keeps its flag (and pill) instead of becoming a plain enrolled check-in.
            const isMakeupCheckIn = enrolment.isTemporary === true && enrolment.attendanceType === "makeup";
            const isWalkInCheckIn = enrolment.isTemporary === true && enrolment.attendanceType === "walkin";
            const response = isShortCourse()
                ? await classApi.checkInShortCourse(selectedClassId, id, selectedDate)
                : await classApi.checkIn(selectedClassId, id, isMakeupCheckIn, isWalkInCheckIn, selectedDate);
            if (!response.ok) {
                setCheckedInIds(prev => {
                    const next = new Set(prev);
                    next.delete(id);
                    return next;
                });
                toast.error("Check-in failed. Please try again.");
            } else if (enrolment.status === "unconfirmed") {
                setEnrolments(prev => prev.map(e => (e.id === id ? { ...e, status: "confirmed" } : e)));
            }
        } catch {
            setCheckedInIds(prev => {
                const next = new Set(prev);
                next.delete(id);
                return next;
            });
            toast.error("Check-in failed. Please try again.");
        }
    };

    const doUndoCheckIn = async enrolment => {
        const id = enrolment.id;
        setConfirmModal(null);
        setCheckedInIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
        try {
            const response = isShortCourse()
                ? await classApi.undoCheckInShortCourse(selectedClassId, id, selectedDate)
                : await classApi.undoCheckIn(selectedClassId, id, selectedDate);
            if (!response.ok) {
                setCheckedInIds(prev => new Set([...prev, id]));
                toast.error("Undo check-in failed. Please try again.");
            } else if (enrolment.isTemporary) {
                // Temporary enrolments are deleted entirely on undo — refresh the list
                // so the student disappears from the roster rather than showing as "not checked in"
                setTimeout(() => loadEnrolments(selectedClassId, selectedDate), 300);
            }
        } catch {
            setCheckedInIds(prev => new Set([...prev, id]));
            toast.error("Undo check-in failed. Please try again.");
        }
    };

    const handleUndoCheckIn = async enrolment => {
        if (isLocked) return;
        const student = enrolment.student || {};
        const name = `${student.firstName || ""} ${student.lastName || ""}`.trim() || "this student";
        setConfirmModal({
            title: "Undo Check-In?",
            message: `Remove check-in for ${name}?`,
            variant: "purple",
            onConfirm: () => doUndoCheckIn(enrolment),
        });
    };

    const handlePayment = enrolment => {
        const student = enrolment.student || {};
        const studentName = `${student.firstName || ""} ${student.lastName || ""}`.trim() || "Student";
        const isTrial = enrolment.attendanceType === "trial";
        setPaymentModalStudent({
            studentProfileId: enrolment.studentProfileId,
            studentName,
            isTrial,
            trialIsPaid: isTrial && enrolment.paymentStatus === "paid",
        });
        setShowPaymentModal(true);
    };

    const reloadRoster = () => {
        if (isShortCourse()) {
            loadShortCourseEnrolments(selectedClassId, selectedDate);
        } else {
            loadEnrolments(selectedClassId, selectedDate);
        }
    };

    // Walk-in blue icon → create the drop-in charge as a manual adjustment (amount only).
    const handleCreateAdjustment = enrolment => {
        const student = enrolment.student || {};
        const studentName = `${student.firstName || ""} ${student.lastName || ""}`.trim() || "Student";
        setCreateAdjustmentFor({
            enrolmentId: enrolment.id,
            studentProfileId: enrolment.studentProfileId,
            studentName,
        });
    };

    // Walk-in orange icon → collect the existing drop-in charge (mark paid / QR / email).
    const handleCollect = enrolment => {
        const student = enrolment.student || {};
        const studentName = `${student.firstName || ""} ${student.lastName || ""}`.trim() || "Student";
        setCollectFor({
            paymentId: enrolment.paymentId,
            studentName,
            amountMinor: enrolment.paymentAmount,
            currency: enrolment.paymentCurrency,
            paymentStatus: enrolment.paymentStatus,
            paidOutsidePlatform: enrolment.paidOutsidePlatform,
            paymentMethod: enrolment.paymentMethod,
            paidAt: enrolment.paidAt,
            reference: enrolment.paymentReference,
        });
    };

    const selectedClass = tonightClasses.find(c => c.id === selectedClassId);
    const notCheckedIn = enrolments.filter(e => !checkedInIds.has(e.id));
    const checkedIn = enrolments
        .filter(e => checkedInIds.has(e.id))
        .sort((a, b) => {
            const ai = checkedInOrder.current.indexOf(a.id);
            const bi = checkedInOrder.current.indexOf(b.id);
            // Checked in this session (in order) → pre-session check-ins at bottom
            if (ai === -1 && bi === -1) return 0;
            if (ai === -1) return 1;
            if (bi === -1) return -1;
            return ai - bi; // lower index = more recently checked in = first
        });

    const weekDays = getWeekDays();

    // Format the currently selected day as "Tuesday 12th May" so the admin
    // never has to second-guess which date the day-pill they clicked maps to.
    // Rendered in the sidebar header in orange so it's the most prominent
    // date indicator on the page.
    const getSelectedDayLabel = () => {
        if (!selectedDate) return "";
        const d = new Date(selectedDate + "T00:00:00");
        const weekday = d.toLocaleDateString("en-AU", { weekday: "long" });
        const dayNum = d.getDate();
        const month = d.toLocaleDateString("en-AU", { month: "long" });
        // Ordinal suffix: 1st, 2nd, 3rd, 4th-20th, 21st, 22nd, 23rd, 24th-30th, 31st
        const suffix = n => {
            const v = n % 100;
            if (v >= 11 && v <= 13) return "th";
            switch (n % 10) {
                case 1:
                    return "st";
                case 2:
                    return "nd";
                case 3:
                    return "rd";
                default:
                    return "th";
            }
        };
        return `${weekday} ${dayNum}${suffix(dayNum)} ${month}`;
    };

    const isPastDay = selectedDate < todayIso;
    const isLocked = isPastDay && !isShortCourse() && currentSession?.isLocked !== false;

    const handleLockSession = async () => {
        if (!currentSession?.id) return;
        setConfirmModal({
            title: "Lock Session?",
            message: "This session will be marked as final for reporting.",
            variant: "purple",
            onConfirm: () => doLockSession(true),
        });
    };

    const doLockSession = async lock => {
        setConfirmModal(null);
        if (!currentSession?.id) return;
        setIsLockingSession(true);
        try {
            const r = await classApi.lockSession(currentSession.id, lock);
            if (r.ok) {
                setCurrentSession(r.data.session);
                toast.success(lock ? "Session locked" : "Session unlocked");
            } else {
                toast.error(r.data?.message || (lock ? "Failed to lock session" : "Failed to unlock session"));
            }
        } catch {
            toast.error(lock ? "Failed to lock session" : "Failed to unlock session");
        } finally {
            setIsLockingSession(false);
        }
    };

    const handleUnlockSession = () => {
        if (!currentSession?.id) return;
        setConfirmModal({
            title: "Unlock Session?",
            message: "Any changes will affect attendance records.",
            variant: "purple",
            onConfirm: () => doLockSession(false),
        });
    };

    return (
        <div className="class-checkin-screen">
            {/* Top bar */}
            <div className="class-checkin-topbar">
                <div className="class-checkin-topbar-left">
                    <button className="class-checkin-back-btn" onClick={() => navigate("/classes")}>
                        <Icon path={mdiArrowLeft} size={0.9} />
                    </button>
                    <div className="class-checkin-week-strip">
                        {weekDays.map(({ iso, label }) => {
                            const isActive = selectedDate === iso;
                            const isToday = todayIso === iso;
                            const isDayPast = iso < todayIso;
                            let cls = "class-checkin-day-pill";
                            if (isActive) cls += " active";
                            if (isToday) cls += " today";
                            if (isDayPast) cls += " past";
                            return (
                                <button key={iso} className={cls} onClick={() => selectDay(iso)}>
                                    {label}
                                </button>
                            );
                        })}
                    </div>
                </div>
                <div className="class-checkin-title">
                    <Icon path={mdiSchool} size={1} color="var(--dl-color-purple-default)" />
                    <span>Check-in</span>
                    <button
                        className="classes-help-trigger"
                        onClick={() => setHelpOpen(true)}
                        onMouseEnter={() => setHelpIconHovered(true)}
                        onMouseLeave={() => setHelpIconHovered(false)}
                        aria-label="Check-In Help"
                        title="How It Works">
                        <Icon
                            path={helpIconHovered ? mdiHelpCircle : mdiHelpCircleOutline}
                            size="1.62rem"
                            color="var(--dl-color-purple-default)"
                        />
                    </button>
                </div>
                <div className="class-checkin-topbar-actions">
                    <button
                        className="class-checkin-add-btn"
                        onClick={() => setShowAddModal(true)}
                        disabled={!selectedClassId}
                        title="Add or enrol a student">
                        <Icon path={mdiAccountPlus} size={0.9} />
                        <span className="class-checkin-btn-label">Add Student</span>
                    </button>
                    <button
                        className="class-checkin-cancel-btn"
                        onClick={() => setCancelClassesOpen(true)}
                        title="Cancel tonight's classes">
                        <Icon path={mdiCalendarRemove} size={0.9} />
                        <span className="class-checkin-btn-label">Cancel Classes</span>
                    </button>
                </div>
            </div>

            <div className="class-checkin-body">
                {/* Left panel: class navigation */}
                <div className="class-checkin-sidebar">
                    <div className="class-checkin-sidebar-week">{getSelectedDayLabel()}</div>
                    {isLoadingClasses ? (
                        <p className="class-checkin-loading">Loading classes...</p>
                    ) : tonightClasses.length === 0 ? (
                        <p className="class-checkin-empty">No classes scheduled for tonight</p>
                    ) : (
                        (() => {
                            // Group short courses that share a shortCourseGroupId; keep weekly classes flat
                            const items = [];
                            const seenGroups = new Set();
                            tonightClasses.forEach(cls => {
                                if (cls.classType === "short-course" && cls.shortCourseGroupId) {
                                    if (!seenGroups.has(cls.shortCourseGroupId)) {
                                        seenGroups.add(cls.shortCourseGroupId);
                                        items.push({
                                            type: "group-heading",
                                            groupId: cls.shortCourseGroupId,
                                            label: cls.shortCourseGroupName || "Weekender",
                                        });
                                    }
                                }
                                items.push({ type: "class", cls });
                            });
                            return items.map(item => {
                                if (item.type === "group-heading") {
                                    return (
                                        <div key={`grp-${item.groupId}`} className="class-checkin-group-heading">
                                            {item.label}
                                        </div>
                                    );
                                }
                                const cls = item.cls;
                                return (
                                    <button
                                        key={cls.id}
                                        className={`class-checkin-tab${selectedClassId === cls.id ? " active" : ""}${cls.classType === "short-course" ? " short-course" : ""}${cls.shortCourseGroupId ? " grouped" : ""}${cls.isCancelled ? " cancelled" : ""}`}
                                        onClick={() => setSelectedClassId(cls.id)}>
                                        <span className="class-checkin-tab-name">{cls.name}</span>
                                        {cls.isCancelled ? (
                                            <span className="class-checkin-tab-cancelled-pill">Cancelled</span>
                                        ) : cls.classType === "short-course" ? (
                                            <span className="class-checkin-tab-term">Short Course</span>
                                        ) : (
                                            cls.termName && (
                                                <span className="class-checkin-tab-term">{cls.termName}</span>
                                            )
                                        )}
                                        <span className="class-checkin-tab-time">
                                            {cls.startTime} – {cls.endTime}
                                        </span>
                                        {selectedClassId === cls.id && (
                                            <Icon
                                                path={mdiChevronRight}
                                                size={1.2}
                                                className="class-checkin-tab-chevron"
                                            />
                                        )}
                                    </button>
                                );
                            });
                        })()
                    )}
                </div>

                {/* Main panel: student list */}
                <div className={`class-checkin-main${isLocked ? " is-locked" : ""}`}>
                    {selectedClass && (
                        <div className="class-checkin-class-header">
                            {selectedClass.roomName && (
                                <span className="class-checkin-class-meta">{selectedClass.roomName}</span>
                            )}
                            {selectedClass.primaryInstructors && selectedClass.primaryInstructors.length > 0 && (
                                <div className="class-checkin-instructors-row">
                                    <Icon path={mdiDanceBallroom} size={0.75} color="var(--dl-color-purple-default)" />
                                    <span className="class-checkin-instructor-names">
                                        {(currentSession?.actualInstructors?.length
                                            ? currentSession.actualInstructors
                                            : selectedClass.primaryInstructors
                                        )
                                            .map(id => {
                                                const admin = orgAdmins.find(a => a.accountId === id);
                                                return admin ? admin.displayName : null;
                                            })
                                            .filter(Boolean)
                                            .join(", ") || "Instructors assigned"}
                                    </span>
                                    {selectedClass.classType !== "short-course" && (
                                        <button
                                            className="btn-turquoise class-checkin-override-btn"
                                            onClick={() => setShowOverrideModal(true)}>
                                            Override Instructor
                                        </button>
                                    )}
                                </div>
                            )}
                            {selectedClass.classType !== "short-course" &&
                                isPastDay &&
                                sessionExists &&
                                currentSession && (
                                    <div className="class-checkin-instructors-row">
                                        {isLocked ? (
                                            <button
                                                className="class-checkin-lock-btn"
                                                onClick={handleUnlockSession}
                                                disabled={isLockingSession}
                                                title="Unlock to edit past session">
                                                <Icon path={mdiLockOutline} size={0.8} />
                                                {isLockingSession ? "Unlocking..." : "Unlock to Edit"}
                                            </button>
                                        ) : (
                                            <button
                                                className="class-checkin-lock-btn"
                                                onClick={handleLockSession}
                                                disabled={isLockingSession}
                                                title="Lock session as final">
                                                <Icon path={mdiLockOpenOutline} size={0.8} />
                                                {isLockingSession ? "Locking..." : "Lock Session"}
                                            </button>
                                        )}
                                    </div>
                                )}
                            {selectedClass.classType === "short-course" && selectedClass.guestInstructorName && (
                                <div className="class-checkin-instructors-row">
                                    <Icon path={mdiDanceBallroom} size={0.75} color="var(--dl-color-purple-default)" />
                                    <span className="class-checkin-instructor-names">
                                        Guest: {selectedClass.guestInstructorName}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}

                    {selectedClass?.isCancelled ? (
                        <div className="class-checkin-cancelled-notice">
                            <Icon path={mdiCalendarRemove} size={1.4} color="var(--dl-color-purple-default)" />
                            <p className="class-checkin-cancelled-title">This class was cancelled</p>
                            {selectedClass.cancellationReason && (
                                <p className="class-checkin-cancelled-reason">{selectedClass.cancellationReason}</p>
                            )}
                        </div>
                    ) : isLoadingEnrolments ? (
                        <p className="class-checkin-loading">Loading students...</p>
                    ) : (
                        <>
                            {/* Start Session gate — weekly classes always; short courses only if primaryInstructors are set */}
                            {!sessionExists && (!isShortCourse() || hasPrimaryInstructors()) ? (
                                <div className="class-checkin-start-session-wrap">
                                    <button
                                        className="class-checkin-start-session-btn"
                                        onClick={handleStartSession}
                                        disabled={isStartingSession}>
                                        <Icon path={mdiSchool} size={1.4} color="var(--dl-color-purple-default)" />
                                        <span className="class-checkin-start-session-label">
                                            {isStartingSession ? "Starting..." : "Start Session"}
                                        </span>
                                        <span className="class-checkin-start-session-sub">
                                            Confirm instructor and load students
                                        </span>
                                    </button>
                                </div>
                            ) : (
                                <>
                                    {isLocked && (
                                        <div className="class-checkin-locked-banner">
                                            <Icon path={mdiLockOutline} size={0.85} color="var(--dl-color-gray-500)" />
                                            <span>
                                                Past session is locked. Use <strong>Unlock to Edit</strong> to make
                                                changes.
                                            </span>
                                        </div>
                                    )}
                                    {/* Not checked in */}
                                    {notCheckedIn.length === 0 && checkedIn.length === 0 && (
                                        <p className="class-checkin-empty">No students enrolled in this class</p>
                                    )}
                                    {notCheckedIn.map(enrolment => (
                                        <ClassStudentCard
                                            key={enrolment.id}
                                            enrolment={enrolment}
                                            onCheckIn={handleCheckIn}
                                            onPayment={handlePayment}
                                            onCreateAdjustment={handleCreateAdjustment}
                                            onCollect={handleCollect}
                                            onRoleClick={e => setChangeRoleEnrolment(e)}
                                            isCheckedIn={false}
                                            isFlashing={flashingId === enrolment.id}
                                            cardRef={el => {
                                                if (el) cardRefs.current[enrolment.id] = el;
                                                else delete cardRefs.current[enrolment.id];
                                            }}
                                        />
                                    ))}

                                    {/* Checked-in divider */}
                                    {checkedIn.length > 0 && (
                                        <div className="class-checkin-divider">Checked in ({checkedIn.length})</div>
                                    )}
                                    {checkedIn.map(enrolment => (
                                        <ClassStudentCard
                                            key={enrolment.id}
                                            enrolment={enrolment}
                                            onCheckIn={handleCheckIn}
                                            onPayment={handlePayment}
                                            onCreateAdjustment={handleCreateAdjustment}
                                            onCollect={handleCollect}
                                            onUndoCheckIn={handleUndoCheckIn}
                                            onRoleClick={e => setChangeRoleEnrolment(e)}
                                            isCheckedIn={true}
                                            cardRef={el => {
                                                if (el) cardRefs.current[enrolment.id] = el;
                                                else delete cardRefs.current[enrolment.id];
                                            }}
                                        />
                                    ))}
                                </>
                            )}
                        </>
                    )}
                </div>

                {/* Stats panel */}
                {selectedClassId && (
                    <div className="class-checkin-stats-panel">
                        <div className="class-checkin-stats-title">Session Stats</div>

                        <div className="class-checkin-stat-block">
                            <span className="class-checkin-stat-label">Enrolled</span>
                            <span className="class-checkin-stat-value">
                                {enrolments.filter(e => !e.isTemporary).length}
                            </span>
                        </div>

                        <div className="class-checkin-stat-block checked">
                            <span className="class-checkin-stat-label">Checked In</span>
                            <span className="class-checkin-stat-value">{checkedInIds.size}</span>
                        </div>

                        <div className="class-checkin-stat-block not-arrived">
                            <span className="class-checkin-stat-label">Yet to Arrive</span>
                            <span className="class-checkin-stat-value">
                                {enrolments.filter(e => !e.isTemporary && !checkedInIds.has(e.id)).length}
                            </span>
                        </div>

                        {enrolments.length > 0 && (
                            <>
                                <div className="class-checkin-progress-bar">
                                    <div
                                        className="class-checkin-progress-fill"
                                        style={{
                                            width: `${Math.round((checkedInIds.size / enrolments.length) * 100)}%`,
                                        }}
                                    />
                                </div>
                                {(() => {
                                    const leaders = enrolments.filter(e => e.role === "leader");
                                    const followers = enrolments.filter(e => e.role === "follower");
                                    const checkedInLeaders = leaders.filter(e => checkedInIds.has(e.id)).length;
                                    const checkedInFollowers = followers.filter(e => checkedInIds.has(e.id)).length;
                                    if (leaders.length === 0 && followers.length === 0) return null;
                                    return (
                                        <div className="class-checkin-stats-footer">
                                            {leaders.length > 0 && (
                                                <div className="class-checkin-role-stat leader">
                                                    <span className="class-checkin-role-label">
                                                        <span className="class-checkin-role-pill-legend leader">L</span>
                                                        <span className="class-checkin-role-count">
                                                            {checkedInLeaders}/{leaders.length}
                                                        </span>
                                                        Leaders
                                                    </span>
                                                </div>
                                            )}
                                            {followers.length > 0 && (
                                                <div className="class-checkin-role-stat follower">
                                                    <span className="class-checkin-role-label">
                                                        <span className="class-checkin-role-pill-legend follower">
                                                            F
                                                        </span>
                                                        <span className="class-checkin-role-count">
                                                            {checkedInFollowers}/{followers.length}
                                                        </span>
                                                        Followers
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </>
                        )}
                        <hr className="class-checkin-legend-divider" />
                        <div className="class-checkin-payment-legend">
                            <strong>Payment icons</strong>
                            <div className="class-checkin-legend-rows">
                                <div className="class-checkin-legend-row">
                                    <Icon
                                        path={mdiCreditCardCheck}
                                        size={0.9}
                                        color="var(--dl-color-greentick)"
                                    />
                                    <span>Fully paid</span>
                                </div>
                                <div className="class-checkin-legend-row">
                                    <Icon
                                        path={mdiCreditCardSyncOutline}
                                        size={0.9}
                                        color="var(--dl-color-greentick)"
                                    />
                                    <span>Paid externally (reconcilable)</span>
                                </div>
                                <div className="class-checkin-legend-row">
                                    <Icon
                                        path={mdiCreditCardClockOutline}
                                        size={0.9}
                                        color="var(--dl-color-purple-900)"
                                    />
                                    <span>Payment plan in progress</span>
                                </div>
                                <div className="class-checkin-legend-row">
                                    <Icon
                                        path={mdiCreditCardPlusOutline}
                                        size={0.9}
                                        color="var(--dl-color-blue-default)"
                                    />
                                    <span>Assign price package</span>
                                </div>
                                <div className="class-checkin-legend-row">
                                    <Icon
                                        path={mdiCreditCardClockOutline}
                                        size={0.9}
                                        color="var(--dl-color-needs-action)"
                                    />
                                    <span>Payment outstanding</span>
                                </div>
                            </div>
                            <span className="class-checkin-legend-tap-hint">To record payment, tap icon on card</span>
                        </div>
                        <div className="class-checkin-payment-legend">
                            <span className="class-student-role-pill unset" style={{ pointerEvents: "none" }}>
                                ?
                            </span>
                            <div style={{ marginBottom: 6 }} />
                            <strong>Role</strong> No leader/follower role assigned yet. Tap the icon on a student card
                            to assign for this class.
                        </div>
                    </div>
                )}
            </div>

            {showAddModal && selectedClassId && (
                <AddStudentModal
                    classId={selectedClassId}
                    termId={selectedClass?.termId}
                    shortCourseId={selectedClass?.classType === "short-course" ? selectedClassId : undefined}
                    enrolledStudentIds={new Set(enrolments.map(e => e.studentProfileId).filter(Boolean))}
                    isPastDay={isPastDay}
                    defaultDate={selectedDate}
                    onClose={() => setShowAddModal(false)}
                    onStudentAdded={() => {
                        setShowAddModal(false);
                        loadEnrolments(selectedClassId, selectedDate);
                    }}
                />
            )}

            {cancelClassesOpen && (
                <CancelClassesModal
                    isOpen={cancelClassesOpen}
                    onClose={() => setCancelClassesOpen(false)}
                    defaultDate={selectedDate}
                    onComplete={() => {
                        loadTonightClasses(selectedDate);
                        if (selectedClassId) loadEnrolments(selectedClassId, selectedDate);
                    }}
                />
            )}

            {showPaymentModal && paymentModalStudent && selectedClass && (
                <AdminPaymentModal
                    isOpen={showPaymentModal}
                    onClose={() => {
                        setShowPaymentModal(false);
                        setPaymentModalStudent(null);
                    }}
                    onSuccess={() => {
                        setShowPaymentModal(false);
                        setPaymentModalStudent(null);
                        if (isShortCourse()) {
                            loadShortCourseEnrolments(selectedClassId, selectedDate);
                        } else {
                            loadEnrolments(selectedClassId, selectedDate);
                        }
                    }}
                    studentProfileId={paymentModalStudent.studentProfileId}
                    orgId={selectedClass.orgId}
                    termId={selectedClass?.termId}
                    studentName={paymentModalStudent.studentName}
                    trialMode={paymentModalStudent.isTrial || false}
                    trialIsPaid={paymentModalStudent.trialIsPaid || false}
                />
            )}

            {createAdjustmentFor && (
                <ManualChargeModal
                    isOpen={!!createAdjustmentFor}
                    onClose={() => setCreateAdjustmentFor(null)}
                    studentProfileId={createAdjustmentFor.studentProfileId}
                    studentName={createAdjustmentFor.studentName}
                    enrolmentId={createAdjustmentFor.enrolmentId}
                    onSuccess={() => {
                        setCreateAdjustmentFor(null);
                        reloadRoster();
                    }}
                />
            )}

            {collectFor && (
                <CollectAdjustmentModal
                    isOpen={!!collectFor}
                    onClose={() => setCollectFor(null)}
                    paymentId={collectFor.paymentId}
                    orgId={selectedClass?.orgId}
                    studentName={collectFor.studentName}
                    amountMinor={collectFor.amountMinor}
                    currency={collectFor.currency}
                    paymentStatus={collectFor.paymentStatus}
                    paidOutsidePlatform={collectFor.paidOutsidePlatform}
                    paymentMethod={collectFor.paymentMethod}
                    paidAt={collectFor.paidAt}
                    reference={collectFor.reference}
                    onSettled={() => {
                        setCollectFor(null);
                        reloadRoster();
                    }}
                />
            )}

            {changeRoleEnrolment && (
                <ChangeRoleModal
                    enrolment={changeRoleEnrolment}
                    onClose={() => setChangeRoleEnrolment(null)}
                    onRoleChanged={() => {
                        setChangeRoleEnrolment(null);
                        loadEnrolments(selectedClassId, selectedDate);
                    }}
                />
            )}

            {showOverrideModal && selectedClassId && selectedClass && (
                <SessionOverrideModal
                    classId={selectedClassId}
                    className={selectedClass.name}
                    sessionDate={selectedDate}
                    allAdmins={orgAdmins}
                    onClose={saved => {
                        setShowOverrideModal(false);
                        if (saved) loadEnrolments(selectedClassId, selectedDate);
                    }}
                />
            )}

            <CheckInHelpModal isOpen={helpOpen} onClose={() => setHelpOpen(false)} />

            {confirmModal && (
                <ConfirmationModal
                    isOpen
                    title={confirmModal.title}
                    message={confirmModal.message}
                    variant={confirmModal.variant}
                    onConfirm={confirmModal.onConfirm}
                    onCancel={() => setConfirmModal(null)}
                />
            )}
        </div>
    );
};

const ClassStudentCard = ({
    enrolment,
    onCheckIn,
    onPayment,
    onCreateAdjustment,
    onCollect,
    onUndoCheckIn,
    onRoleClick,
    isCheckedIn,
    isFlashing,
    cardRef,
}) => {
    const student = enrolment.student || {};
    const firstName = student.firstName || "";
    const lastName = student.lastName || "";

    const [photoUrl, setPhotoUrl] = useState(null);
    const [isPhotoLoading, setIsPhotoLoading] = useState(false);
    const [avatarKey, setAvatarKey] = useState(student.avatarKey || null);
    const [photoVersion, setPhotoVersion] = useState(student.studentPhotoVersion || null);
    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const [forcePickerOpen, setForcePickerOpen] = useState(0);

    useEffect(() => {
        if (!avatarKey) return;
        const studentId = student.id;
        if (!studentId) return;

        // For student-profile/claimed-account sources photoVersion is set; for account-only
        // sources it is never set — use avatarKey (the imageKey filename) as the discriminator
        // so the cache busts when the user updates their account avatar.
        const cacheKey =
            photoVersion != null ? `student_${studentId}_v${photoVersion}` : `student_account_${avatarKey}`;

        // Synchronous check — memory cache hit means no loading state needed
        const cached = getCachedImage(cacheKey, "student-photo");
        if (cached) {
            setPhotoUrl(base64ToBlobUrl(cached));
            return;
        }

        let cancelled = false;
        setIsPhotoLoading(true);
        getOrFetchImage(cacheKey, "student-photo", () =>
            classApi
                .getStudentPhotoBase64(studentId)
                .then(resp => (resp.ok && resp.data?.imageBase64 ? resp.data.imageBase64 : null))
        )
            .then(base64 => {
                if (!cancelled && base64) setPhotoUrl(base64ToBlobUrl(base64));
            })
            .finally(() => {
                if (!cancelled) setIsPhotoLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [avatarKey, photoVersion]); // eslint-disable-line react-hooks/exhaustive-deps
    const fullName = `${firstName} ${lastName}`.trim() || "Unknown Student";
    const status = enrolment.status;
    // 4-state payment icon model — mirrors ClassStudentsPage so the same icon means
    // the same thing on both screens (see class-payments.service.getOutstandingPaymentSet).
    const isInstalmentIncomplete =
        enrolment.instalmentsTotal > 1 && enrolment.instalmentsPaid < enrolment.instalmentsTotal;
    // partial-refund is net-paid (money paid, only part refunded) — counts as PAID,
    // matching backend PAID_STATUSES and ClassStudentsPage. Without this the roster
    // shows a partial-refund enrolment as orange "outstanding" while the student page
    // correctly shows it green/paid.
    const isPaid =
        (enrolment.paymentStatus === "paid" || enrolment.paymentStatus === "partial-refund") &&
        !isInstalmentIncomplete;
    // Purple clock: only for instalment plans actively in progress (some paid, more expected)
    const isInstalmentInProgress =
        !isPaid &&
        enrolment.instalmentsTotal > 1 &&
        enrolment.instalmentsPaid > 0 &&
        enrolment.instalmentsPaid < enrolment.instalmentsTotal;
    // Sync icon: paid via cash/bank, awaiting bank-statement reconciliation
    const isExternalPaid = isPaid && enrolment.paidOutsidePlatform === true;
    // School-wide outstanding-payment flag — downgrades green to orange when the
    // student has any unpaid record at the school, even on a different class/term
    const hasHistoricalOutstanding = student.hasOutstandingPayments === true;
    // Green solid check only when fully confirmed: paid, no historical outstanding, no external
    const effectiveIsPaid = isPaid && !hasHistoricalOutstanding && !isExternalPaid;
    // Walk-in / make-up are per-session, not term enrolments (see pill logic below). Their
    // payment is NOT a term package: a make-up is covered by the student's term enrolment
    // (hide the payment icon entirely), and a walk-in owes a one-off drop-in fee (the icon
    // opens a manual charge bound to this enrolment, not the term-package modal).
    const isWalkIn = enrolment.isWalkInToday || (enrolment.isTemporary && enrolment.attendanceType === "walkin");
    const isMakeup = enrolment.isMakeupToday || (enrolment.isTemporary && enrolment.attendanceType === "makeup");
    let paymentIconPath;
    let paymentIconColor;
    let paymentTitle;
    let paymentIsPaidStyle = false;
    if (isExternalPaid) {
        paymentIconPath = mdiCreditCardSyncOutline;
        paymentIconColor = "var(--dl-color-greentick)";
        paymentTitle = "Paid externally. Reconcile in Manage Finances whenever you balance your books (optional).";
        paymentIsPaidStyle = true;
    } else if (effectiveIsPaid) {
        paymentIconPath = mdiCreditCardCheck;
        paymentIconColor = "var(--dl-color-greentick)";
        paymentTitle = "Paid";
        paymentIsPaidStyle = true;
    } else if (isInstalmentInProgress) {
        paymentIconPath = mdiCreditCardClockOutline;
        paymentIconColor = "var(--dl-color-purple-900)";
        paymentTitle = "Payment plan in progress";
    } else if (enrolment.paymentStatus === null) {
        // No payment recorded yet. Walk-in = add a one-off drop-in charge (adjustment);
        // normal enrolment = assign a term price package.
        paymentIconPath = mdiCreditCardPlusOutline;
        paymentIconColor = "var(--dl-color-blue-default)";
        paymentTitle = isWalkIn ? "Add drop-in charge" : "Assign price package";
    } else {
        paymentIconPath = mdiCreditCardClockOutline;
        paymentIconColor = "var(--dl-color-needs-action)";
        paymentTitle = isWalkIn ? "Collect drop-in payment" : "Payment outstanding";
    }
    let pillLabel, pillClass;
    // isWalkIn / isMakeup are computed above. Keying the pill off the attendance type (not
    // just the checked-in *Today flags) means an "expected" (added-but-not-yet-arrived)
    // walk-in still reads as a walk-in rather than falling through to the green "Enrolled" pill.
    if (isMakeup) {
        pillLabel = "Make-up";
        pillClass = "makeup";
    } else if (isWalkIn) {
        pillLabel = "Walk-in";
        pillClass = "walkin";
    } else if (enrolment.attendanceType === "trial") {
        pillLabel = "Trial Deal";
        pillClass = "trial";
    } else if (status === "confirmed") {
        pillLabel = "Enrolled";
        pillClass = "enrolled";
    } else if (status === "unconfirmed") {
        pillLabel = "Unconfirmed \u2014 check in to confirm";
        pillClass = "unconfirmed";
    }

    return (
        <div
            ref={cardRef}
            className={`class-student-card${isCheckedIn ? " checked-in" : ""}${isFlashing ? " check-success" : ""}`}>
            <div className="class-student-card-avatar">
                {isPhotoLoading ? (
                    <div className="class-student-avatar-loading" />
                ) : (
                    <StudentAvatarUpload
                        currentImage={photoUrl}
                        studentProfileId={student.id}
                        forcePickerOpen={forcePickerOpen}
                        onPreviewClick={() => setShowPreviewModal(true)}
                        onSaveSuccess={(newBase64, responseData) => {
                            const newKey = responseData?.profile?.studentProfileImageKey;
                            const newVersion = responseData?.profile?.studentPhotoVersion;
                            setAvatarKey(newKey || avatarKey);
                            setPhotoVersion(newVersion || photoVersion);
                            setPhotoUrl(newBase64);
                            if (newKey && newVersion) {
                                const ck = `student_${student.id}_v${newVersion}`;
                                cacheImage(ck, newBase64, "student-photo").catch(() => {});
                            }
                        }}
                        onRemove={() => {
                            setPhotoUrl(null);
                            setAvatarKey(null);
                            setPhotoVersion(null);
                        }}
                        onSaveError={() => {}}
                    />
                )}
            </div>
            {showPreviewModal && (
                <StudentPhotoPreviewModal
                    isOpen={showPreviewModal}
                    onClose={() => setShowPreviewModal(false)}
                    photoUrl={photoUrl}
                    studentName={`${firstName} ${lastName}`.trim()}
                    onChangePhoto={() => setForcePickerOpen(prev => prev + 1)}
                    onRemovePhoto={async () => {
                        const resp = await classApi.deleteStudentPhoto(student.id);
                        if (resp.ok) {
                            setPhotoUrl(null);
                            setAvatarKey(null);
                            setPhotoVersion(null);
                            toast.success("Photo removed.");
                        } else {
                            toast.error(resp.data?.message || "Failed to remove photo.");
                        }
                    }}
                />
            )}

            <div className="class-student-card-info">
                <span className="class-student-name">{fullName}</span>
            </div>

            <div className="class-student-card-status">
                <span className={`class-student-status-pill ${pillClass}`}>{pillLabel}</span>
                <button
                    className={`class-student-role-pill ${enrolment.role || "unset"}`}
                    onClick={e => {
                        e.stopPropagation();
                        onRoleClick(enrolment);
                    }}
                    title={enrolment.role ? `Change role (currently ${enrolment.role})` : "Set role"}>
                    {enrolment.role === "leader" ? "L" : enrolment.role === "follower" ? "F" : "?"}
                </button>
            </div>

            {/* Make-up is covered by the student's term enrolment, so it carries no payment
                of its own — hide the icon. Walk-in = drop-in adjustment lifecycle: no charge →
                create it (blue), outstanding → collect it via mark-paid/QR/email (orange), never
                auto-fires. Everyone else uses the term price-package modal. */}
            {onPayment && status !== "unconfirmed" && !isMakeup && (
                <button
                    className={`class-student-payment-btn${paymentIsPaidStyle ? " class-student-payment-btn--paid" : ""}`}
                    onClick={() => {
                        if (!isWalkIn) { onPayment(enrolment); return; }
                        if (enrolment.paymentStatus === null) { onCreateAdjustment && onCreateAdjustment(enrolment); }
                        else { onCollect && onCollect(enrolment); } // outstanding → collect; paid → view summary
                    }}
                    title={paymentTitle}>
                    <Icon path={paymentIconPath} size={1.2} color={paymentIconColor} />
                </button>
            )}

            {!isCheckedIn && (
                <button
                    className={`class-student-checkin-btn${isFlashing ? " check-bounce" : ""}`}
                    onClick={() => onCheckIn(enrolment)}
                    title="Check in"
                    style={{ marginLeft: "6px" }}>
                    <Icon path={mdiCheckCircleOutline} size={1.8} color="var(--dl-color-needs-action)" />
                </button>
            )}

            {isCheckedIn && (
                <div className="class-student-checked-indicator">
                    <Icon path={mdiCheckCircle} size={1.5} color="#2e7d32" />
                    <button
                        className="class-student-undo-btn"
                        title="Undo check-in"
                        onClick={() => onUndoCheckIn(enrolment)}>
                        <Icon path={mdiArrowULeftTop} size={1.1} color="var(--dl-color-purple-900)" />
                    </button>
                </div>
            )}
        </div>
    );
};

export default ClassCheckIn;
